import { Check, Sparkles, Car, Crown } from "lucide-react";

const PACKAGES = [
  {
    id: "interior",
    icon: Car,
    name: "Interior Detail",
    price: 120,
    duration: "2 – 3 hrs",
    blurb: "A deep reset for the cabin — every crevice, surface and stitch.",
    features: [
      "Full vacuum (seats, carpets, trunk)",
      "Dashboard, console & vent dusting",
      "Interior window cleaning",
      "Leather conditioning / fabric shampoo",
      "Stain spot treatment",
      "Air freshener finish",
    ],
  },
  {
    id: "exterior",
    icon: Sparkles,
    name: "Exterior Detail",
    price: 100,
    duration: "1.5 – 2 hrs",
    blurb: "Foam wash, decontamination, and a deep gloss finish on every panel.",
    features: [
      "pH-balanced foam cannon wash",
      "Two-bucket hand wash & dry",
      "Wheel, tire & wheel-well cleaning",
      "Tire shine application",
      "Spray wax / sealant finish",
      "Exterior window & trim wipe",
    ],
  },
  {
    id: "premium",
    icon: Crown,
    name: "Premium Full Detail",
    price: 250,
    duration: "4 – 6 hrs",
    blurb: "Everything we do — combined into one bumper-to-bumper restoration.",
    featured: true,
    features: [
      "Complete interior + exterior detail",
      "Paint-safe deep clean",
      "Ceramic spray sealant (3-month)",
      "Engine bay wipe-down",
    ],
  },
];

export default function Services() {
  const goBook = (pkg) => {
    const el = document.getElementById("booking");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    // notify booking form
    window.dispatchEvent(new CustomEvent("booking:set-package", { detail: pkg }));
  };

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 lg:py-32 bg-[#0A0A0A]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-6">
          <span className="divider-red" />
          <span className="section-label">Our Packages</span>
        </div>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tighter max-w-2xl">
            Three ways to make your car
            <span className="text-[#E63946]"> look new.</span>
          </h2>
          <p className="text-zinc-400 max-w-md text-base leading-relaxed">
            Pick a service, set a time, we come to you. All packages use professional
            products and the same level of obsessive care.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PACKAGES.map((p) => {
            const Icon = p.icon;
            return (
              <article
                key={p.id}
                data-testid={`service-card-${p.id}`}
                className={`service-card ${p.featured ? "featured" : ""} p-8 flex flex-col`}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="w-11 h-11 flex items-center justify-center bg-white/5 border border-white/10">
                    <Icon className="w-5 h-5 text-[#E63946]" />
                  </div>
                  {p.featured && (
                    <span className="section-label text-[#E63946]">Most Popular</span>
                  )}
                </div>

                <h3 className="font-display font-semibold text-2xl tracking-tight">
                  {p.name}
                </h3>
                <p className="mt-2 text-zinc-400 text-sm leading-relaxed">{p.blurb}</p>

                <div className="mt-6 flex items-baseline gap-2">
                  <span className="section-label">Starting at</span>
                </div>
                <div className="flex items-baseline gap-3 mt-1">
                  <span
                    data-testid={`service-price-${p.id}`}
                    className="font-display font-bold text-4xl tracking-tighter"
                  >
                    ${p.price}
                  </span>
                  <span className="text-zinc-500 text-sm">/ {p.duration}</span>
                </div>

                <ul className="mt-7 space-y-3 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-zinc-300">
                      <Check className="w-4 h-4 text-[#E63946] mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  data-testid={`service-book-${p.id}`}
                  onClick={() => goBook(p.id)}
                  className={`mt-8 w-full px-5 py-3 font-semibold tracking-wide transition-colors ${
                    p.featured
                      ? "bg-[#E63946] hover:bg-[#F0545E] text-white"
                      : "border border-white/15 hover:border-white text-white"
                  }`}
                >
                  Book {p.name.split(" ")[0]}
                </button>
              </article>
            );
          })}
        </div>

        <div className="mt-10 flex items-start gap-4 border-l-2 border-[#E63946] bg-[#141414] px-5 py-4">
          <span className="section-label text-[#E63946] mt-0.5 shrink-0">Important</span>
          <p className="text-sm text-zinc-300 leading-relaxed">
            As a mobile service, customers must provide access to a working{" "}
            <span className="text-white font-semibold">water spigot</span> and an{" "}
            <span className="text-white font-semibold">electrical outlet</span> at the
            service location.
          </p>
        </div>

        <p className="mt-6 text-xs text-zinc-500">
          * Final pricing may vary based on vehicle size and condition. Larger SUVs,
          trucks and excessive soil may incur an additional charge.
        </p>
      </div>
    </section>
  );
}
